// ✅ src/pages/OrderManagement/SecuritiesScreen.jsx  (FULL FILE - UPDATED)
// ✅ Uses safe localStorage helper
// ✅ Adds finYearId, finYear, branchId, branchType, loginUserId
// ✅ Adds commonHeaders in API calls
// ✅ Backend mount:
//   app.use("/api/securities", securities);
//
// Endpoints used:
//   GET https://nissi-india.com/api/securities/list?fromDate=&toDate=&srcId=&contractName=
//
// Notes:
// - Keeps your existing UI
// - Sends localStorage headers in API call
// - View All clears filters and reloads

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../../components/ProjectPostOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaEye, FaChevronUp, FaChevronDown } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/securities";

const toDMY = (dateStr) => {
  // accepts: "", dd/MM/yyyy, yyyy-MM-dd, ISO
  if (!dateStr) return "";
  const s = String(dateStr).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const money2 = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  if (!Number.isFinite(n)) return v ?? "";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const toInt = (v, fallback = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : fallback;
};

const toStr = (v, fallback = "") => {
  if (v === null || v === undefined) return fallback;
  return String(v).trim();
};

// ✅ helper: safely pick a value from possible DB column names
const pick = (row, keys, fallback = "") => {
  for (const k of keys) {
    if (
      row?.[k] !== undefined &&
      row?.[k] !== null &&
      String(row[k]).trim() !== ""
    ) {
      return row[k];
    }
  }
  return fallback;
};

export default function SecuritiesScreen() {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    try {
      if (typeof window === "undefined") return fallback;
      const val = localStorage.getItem(key);
      return val !== null && val !== undefined ? val : fallback;
    } catch (err) {
      return fallback;
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");

  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(() => toStr(branchTypeLS || "HO"), [branchTypeLS]);
  const loginUserId = useMemo(() => toStr(userIdLS), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    srcId: "",
    contractName: "",
  });

  // Table data
  const [rows, setRows] = useState([]);

  // ✅ Load initial data once
  useEffect(() => {
    loadData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((p) => ({ ...p, [field]: value }));
  };

  const buildParams = (f) => ({
    fromDate: f.fromDate || "",
    toDate: f.toDate || "",
    srcId: f.srcId || "",
    contractName: f.contractName || "",
  });

  const loadData = async (f) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/list`, {
        params: buildParams(f),
        headers: commonHeaders,
      });

      const data = res.data?.data || [];
      setRows(Array.isArray(data) ? data : []);

      if ((data || []).length === 0) {
        toast.info("No records found");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load securities");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadData(filters);
  };

  const handleViewAll = () => {
    const cleared = {
      fromDate: "",
      toDate: "",
      srcId: "",
      contractName: "",
    };
    setFilters(cleared);
    loadData(cleared);
  };

  // ✅ Normalize DB rows for your UI columns
  const uiRows = useMemo(() => {
    return (rows || []).map((r, idx) => {
      const startDate = pick(
        r,
        ["StartDate", "fld_startdate", "fld_date", "Start_Date", "startdate"],
        ""
      );
      const srcId = pick(
        r,
        ["SRCIdNo", "fld_srcidno", "fld_srcid", "srcidno", "srcid"],
        ""
      );
      const amount = pick(
        r,
        ["Amount", "fld_amount", "fld_emdamount", "emdamount", "bidsecurityamount"],
        ""
      );
      const validFrom = pick(
        r,
        ["ValidFrom", "fld_validfrom", "fld_emdvalidfrom", "emdvalidfrom"],
        ""
      );
      const validUpto = pick(
        r,
        ["ValidUpto", "fld_validupto", "fld_emdvalidupto", "emdvalidupto"],
        ""
      );
      const releaseMode = pick(
        r,
        ["ReleaseMode", "fld_releasemode", "fld_emdreleasemode", "emdreleasemode"],
        ""
      );
      const contract = pick(
        r,
        ["ContractName", "fld_contractname", "contractname", "ProjectName"],
        ""
      );

      return {
        sno: idx + 1,
        startDate: toDMY(startDate),
        srcId: String(srcId ?? ""),
        amount: money2(amount),
        validFrom: toDMY(validFrom),
        validUpto: toDMY(validUpto),
        releaseMode: String(releaseMode ?? ""),
        contract: String(contract ?? ""),
      };
    });
  }, [rows]);

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SECURITIES</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black border-gray-200"
          }`}
        >
          {/* Header + Hide/Show */}
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Securities
            </h1>

            <button
              onClick={() => setShowFilters((p) => !p)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    SRC Id No
                  </label>
                  <input
                    type="text"
                    value={filters.srcId}
                    onChange={(e) => handleFilterChange("srcId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="Type SRC Id..."
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contract Name
                  </label>
                  <input
                    type="text"
                    value={filters.contractName}
                    onChange={(e) => handleFilterChange("contractName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="Type Contract..."
                  />
                </div>
              </div>

              <div className="flex justify-center items-center gap-6 mb-8">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? `${buttonClass} ${hoverClass} text-white`
                      : `${buttonClass} ${hoverClass} text-white`
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  onClick={handleViewAll}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? "bg-gray-600 hover:bg-gray-700 text-white"
                      : "bg-gray-500 hover:bg-gray-600 text-white"
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaEye />
                  View All
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Start Date</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">SRC Id No</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      EMD/BID Security Amount
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      EMD Validity From
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      EMD Validity UpTo
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      EMD Mode of Release/Adjustable
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">Contract Name</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {uiRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className={`px-6 py-10 text-center ${
                          isDark ? "text-gray-200" : "text-gray-600"
                        }`}
                      >
                        {loading ? "Loading..." : "No records"}
                      </td>
                    </tr>
                  ) : (
                    uiRows.map((item) => (
                      <tr
                        key={item.sno}
                        className={`transition-colors duration-200 ${
                          isDark
                            ? "bg-gray-800 hover:bg-gray-750"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td
                          className={`px-4 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.sno}
                        </td>
                        <td
                          className={`px-4 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.startDate}
                        </td>
                        <td
                          className={`px-4 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.srcId}
                        </td>
                        <td
                          className={`px-4 py-3 text-right border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.amount}
                        </td>
                        <td
                          className={`px-4 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.validFrom}
                        </td>
                        <td
                          className={`px-4 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.validUpto}
                        </td>
                        <td
                          className={`px-4 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.releaseMode}
                        </td>
                        <td
                          className={`px-4 py-3 text-center ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {item.contract}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <ToastContainer
          position="bottom-right"
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
}